import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { loadCommissions, updateCommissions, computeCommission } from '../actionCreators';
import { addCommission, deleteCommission } from '../actionCreators';
import { getCommissions, getCommissionsFlags, getOfferCurrency } from '../selectors';
import * as editableDocument from 'utils/model/editableDocument';
import { collection } from 'utils/immutable';
import CommissionsComponent from './CommissionsComponent';
import flow from 'lodash.flow';
import useEventCallback from 'utils/hooks/useEventCallback';
import { getCommissionsValidationRules } from '../rules';

const CommissionsContainer = ({ documentId, siteId, numberOfCommissions }) => {
  const [state, setState] = useState({
    commissions: [],
    lastAddedItemIndex: 0,
    showModal: false,
    commissionListIsDirty: false
  });
  const [itemRemoved, setItemRemoved] = useState(false);
  const [calcInProgress, setCalcInProgress] = useState({
    inProgress: false,
    index: null
  });

  const [expandedCommissionsCard, setExpandedCommissionsCard] = useState(false);
  const commissionRules = useMemo(() => getCommissionsValidationRules(state.commissions), [state.commissions]);

  const dispatch = useDispatch();

  const key = `${documentId}-${siteId}`;
  const { loaded, computed, saving } = useSelector(
    state => getCommissionsFlags(state.currentOffer.commissions, state.entities, key),
    shallowEqual
  );

  const commissions = useSelector(state => getCommissions(state.currentOffer.commissions, state.entities, key), shallowEqual);

  const { currencyId, currencyCode } = useSelector(state => getOfferCurrency(state.currentOffer.header), shallowEqual);

  useEffect(() => {
    if (loaded || !expandedCommissionsCard) return;
    dispatch(loadCommissions(documentId, siteId));
  }, [documentId, siteId, loaded, dispatch, expandedCommissionsCard]);

  useEffect(() => {
    if (!loaded) return;
    setState(prev => ({
      ...prev,
      commissions: commissions.map(editableDocument.create)
    }));
    // if you want to remove this warning, check if a commission can be added.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded]);

  useEffect(() => {
    if (computed && computed.isCommissionComputed) {
      const computedCommission = editableDocument.create({ ...commissions[computed.index] });
      setState(prev => ({
        ...prev,
        commissions: collection.update(computed.index, editableDocument.setDirty(computedCommission), prev.commissions)
      }));
    }
  }, [commissions, computed]);

  const changeItemProperty = useCallback(
    (index, prop, value) => {
      if (state.commissions && state.commissions.length < 1) return;

      if (prop === 'commissionPercent') {
        setState(prev => ({
          ...prev,
          commissionListIsDirty: true,
          commissions: collection.update(
            index,
            flow(editableDocument.update(prop, value, null), editableDocument.validate(commissionRules, false)),
            prev.commissions
          )
        }));
        if (state.commissions[index].document.calculationMethodId && !itemRemoved) {
          setCalcInProgress({ inProgress: true, index });
          dispatch(
            computeCommission(
              state.commissions[index].document,
              state.commissions[index].document.commissionId,
              {
                calculationMethodId: state.commissions[index].document.calculationMethodId,
                calculationMethodName: state.commissions[index].document.calculationMethodName
              },
              documentId,
              siteId,
              value,
              state.commissions[index].document.commissionAmount,
              true,
              index,
              false,
              () => {
                setCalcInProgress({ inProgress: false, index });
              }
            )
          );
        }
      } else if (prop === 'commissionAmount') {
        setState(prev => ({
          ...prev,
          commissionListIsDirty: true,
          commissions: collection.update(
            index,
            flow(editableDocument.update(prop, value, null), editableDocument.validate(commissionRules, false)),
            prev.commissions
          )
        }));
        if (state.commissions[index].document.calculationMethodId && !itemRemoved) {
          setCalcInProgress({ inProgress: true, index });
          dispatch(
            computeCommission(
              state.commissions[index].document,
              state.commissions[index].document.commissionId,
              {
                calculationMethodId: state.commissions[index].document.calculationMethodId,
                calculationMethodName: state.commissions[index].document.calculationMethodName
              },
              documentId,
              siteId,
              state.commissions[index].document.commissionPercent,
              value,
              false,
              index,
              false,
              () => {
                setCalcInProgress({ inProgress: false, index });
              }
            )
          );
        }
      } else if (prop === 'partner') {
        setState(prev => ({
          ...prev,
          commissionListIsDirty: true,
          commissions: collection.update(
            index,
            flow(
              editableDocument.update('partnerId', value.dealerId, null),
              editableDocument.update('partnerName', value.name, null),
              editableDocument.validate(commissionRules, false)
            ),
            prev.commissions
          )
        }));
      } else if (prop === 'calculationMethod') {
        if (state.commissions[index].document.commissionPercent && !itemRemoved) {
          setCalcInProgress({ inProgress: true, index });
          dispatch(
            computeCommission(
              state.commissions[index].document,
              value.commissionId ? value.commissionId : state.commissions[index].document.commissionId,
              { calculationMethodId: value.calculationMethodId, calculationMethodName: value.calculationMethodName },
              documentId,
              siteId,
              value.calculationMethodId ? state.commissions[index].document.commissionPercent : null,
              value.calculationMethodId ? state.commissions[index].document.commissionAmount : null,
              true,
              index,
              false,
              () => {
                setCalcInProgress({ inProgress: false, index });
              }
            )
          );
        }
        setState(prev => ({
          ...prev,
          commissionListIsDirty: true,
          commissions: collection.update(
            index,
            flow(
              editableDocument.update('calculationMethodId', value.calculationMethodId, null),
              editableDocument.update('calculationMethodName', value.calculationMethodName, null),
              editableDocument.validate(commissionRules, false)
            ),
            prev.commissions
          )
        }));
      } else {
        setState(prev => ({
          ...prev,
          commissionListIsDirty: true,
          commissions: collection.update(
            index,
            flow(editableDocument.update(prop, value, null), editableDocument.validate(commissionRules, false)),
            prev.commissions
          )
        }));
      }
      setItemRemoved(false);
    },
    [commissionRules, dispatch, documentId, siteId, state.commissions, itemRemoved]
  );

  const saveCommissions = useEventCallback(() => {
    const validationList = state.commissions.map(x => editableDocument.validate(commissionRules, false, x));
    if (validationList.some(x => !x.validationInfo.isValid)) {
      setState(prev => ({
        ...prev,
        commissions: validationList
      }));
      return;
    }

    const commissionsToSave = state.commissions.map(x => x.document);

    dispatch(
      updateCommissions(documentId, siteId, commissionsToSave, false, () => {
        setState(prev => ({
          ...prev,
          commissions: prev.commissions.map(x => {
            return editableDocument.resetDirtyInfo(x);
          }),
          commissionListIsDirty: false
        }));
      })
    );
  }, [state.commissions]);

  const addNewItem = useEventCallback(() => {
    const initialCommission = {
      documentId,
      siteId,
      documentCommissionId: state.lastAddedItemIndex - 1,
      commissionAmount: null,
      commissionPercent: null,
      currencyId: currencyId,
      currencyCode: currencyCode,
      calculationMethodId: null,
      isCommission: true
    };
    dispatch(addCommission(initialCommission));
    setState(prev => ({
      ...prev,
      commissions: collection.insert(prev.commissions.length, editableDocument.create(initialCommission), prev.commissions),
      lastAddedItemIndex: prev.lastAddedItemIndex - 1,
      commissionListIsDirty: true
    }));
  }, []);

  const removeItem = useEventCallback(idx => {
    if (idx === -1) return;
    dispatch(deleteCommission(documentId, siteId, state.commissions[idx]));
    setState(prev => ({
      ...prev,
      commissions: collection.remove(idx, prev.commissions),
      commissionListIsDirty: true
    }));
    setItemRemoved(true);
  }, []);

  return (
    <CommissionsComponent
      commissions={state.commissions}
      numberOfCommissions={numberOfCommissions}
      onSave={saveCommissions}
      onAdd={addNewItem}
      onRemove={removeItem}
      saving={saving.commissions}
      onPropertyChange={changeItemProperty}
      listIsDirty={state.commissionListIsDirty}
      calcInProgress={calcInProgress}
      expandedCommissionsCard={expandedCommissionsCard}
      setExpandedCommissionsCard={setExpandedCommissionsCard}
      loaded={loaded}
    />
  );
};

CommissionsContainer.propTypes = {
  documentId: PropTypes.number,
  siteId: PropTypes.number,
  commissions: PropTypes.array,
  numberOfCommissions: PropTypes.number
};

export default CommissionsContainer;
